import { useEffect, useState } from "react";

import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { Link, useParams } from "react-router-dom";

import useNotification from "../../components/notifications/useNotifications";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { usePartProTradeBidVehicle } from "../../data/usePartProTradeBids";
import {
  IPartProTradeBid,
  IPartProTradeBidLot,
  IPartProTradeBidVehicleDetail,
  PartProTradeVehicleStatus,
} from "../../models/partPro/IPartProTradeBids";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";

const STATUS_PILL: Record<PartProTradeVehicleStatus, { label: string; classes: string }> = {
  DRAFT: { label: "Draft", classes: "bg-gray-100 text-gray-700" },
  OPEN: { label: "Open for bids", classes: "bg-green-100 text-green-800" },
  CLOSED: { label: "Closed - accept bids", classes: "bg-amber-100 text-amber-800" },
  COMPLETED: { label: "Purchased", classes: "bg-blue-100 text-blue-800" },
  WITHDRAWN: { label: "Withdrawn", classes: "bg-red-100 text-red-700" },
};

type PendingAction =
  | { kind: "accept"; lot: IPartProTradeBidLot; bid: IPartProTradeBid }
  | { kind: "pass"; lot: IPartProTradeBidLot }
  | { kind: "end" };

/** datetime-local wants "yyyy-MM-ddTHH:mm" in local time */
const toLocalInput = (iso: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat("yyyy-MM-dd'T'HH:mm") : DateTime.now().plus({ days: 1 }).toFormat("yyyy-MM-dd'T'HH:mm");

function BuyerCell({ bid }: { bid: IPartProTradeBid }) {
  return (
    <div>
      {bid.accountId ? (
        <Link
          to={`/accounts/${bid.accountId}`}
          onClick={(e) => e.stopPropagation()}
          className="font-medium text-primary-600 hover:text-primary-700"
        >
          {bid.accountName}
        </Link>
      ) : (
        <p className="font-medium text-gray-900">{bid.buyerCompanyName}</p>
      )}
      <p className="text-xs text-gray-500">
        {[bid.accountId ? bid.buyerCompanyName : null, bid.buyerEmail, bid.buyerPhone].filter(Boolean).join(" · ")}
      </p>
    </div>
  );
}

function LotCard({
  lot,
  vehicle,
  canDecide,
  busy,
  onAccept,
  onPass,
  onReopen,
}: {
  lot: IPartProTradeBidLot;
  vehicle: IPartProTradeBidVehicleDetail;
  /** Accepting / passing on bids is admin only - awards are final and email the buyer */
  canDecide: boolean;
  busy: boolean;
  onAccept: (bid: IPartProTradeBid) => void;
  onPass: () => void;
  onReopen: () => void;
}) {
  const deciding = canDecide && vehicle.status === "CLOSED" && lot.status === "PENDING";

  return (
    <div className="rounded-lg bg-white shadow">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <h3 className="font-semibold text-gray-900">{lot.categoryName}</h3>
        <div className="flex items-center gap-2">
          {lot.status === "AWARDED" && lot.award && (
            <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              Sold · {PriceFormat(lot.award.amount)}
            </span>
          )}
          {lot.status === "NOT_AWARDED" && (
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">Not awarded</span>
          )}
          {canDecide && lot.status === "NOT_AWARDED" && vehicle.status === "CLOSED" && (
            <ButtonInput label="Change decision" size="sm" isSubmit={false} classes="" onClick={onReopen} disabled={busy} />
          )}
          {deciding && (
            <ButtonInput
              label="Don't award"
              size="sm"
              isSubmit={false}
              classes="border border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-50"
              onClick={onPass}
              disabled={busy}
            />
          )}
        </div>
      </div>

      {lot.status === "AWARDED" && lot.award && (
        <p className="px-4 pt-3 text-sm text-gray-700">
          Sold to{" "}
          {lot.award.accountId ? (
            <Link to={`/accounts/${lot.award.accountId}`} className="font-medium text-primary-600 hover:text-primary-700">
              {lot.award.accountName}
            </Link>
          ) : (
            <span className="font-medium">{lot.award.buyerCompanyName}</span>
          )}{" "}
          on {formatDateTime(lot.award.awardedAt, DateTime.DATE_MED)} · PartPro commission {PriceFormat(lot.award.commission)} (
          {lot.award.commissionPct}%){lot.award.overridden && " · not the highest bid"}
          {lot.award.overrideReason && ` - "${lot.award.overrideReason}"`}
        </p>
      )}

      {lot.bids.length === 0 ? (
        <p className="px-4 py-4 text-sm text-gray-500">No bids on this category.</p>
      ) : (
        <table className="min-w-full text-sm">
          <tbody className="divide-y divide-gray-100">
            {lot.bids.map((bid) => {
              const won = lot.award?.bidId === bid.id;
              return (
                <tr key={bid.id} className={won ? "bg-green-50" : ""}>
                  <td className="w-32 px-4 py-3 font-semibold text-gray-900">
                    {PriceFormat(bid.amount)}
                    {bid.isAutoPick && lot.status === "PENDING" && (
                      <span className="ml-2 rounded bg-primary-50 px-1.5 py-0.5 text-[11px] font-medium text-primary-700">
                        Highest
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <BuyerCell bid={bid} />
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {bid.message && <p className="italic">&ldquo;{bid.message}&rdquo;</p>}
                    <p className="text-xs text-gray-400">
                      Bid {formatDateTime(bid.amountSetAt, DateTime.DATETIME_SHORT)}
                    </p>
                  </td>
                  <td className="w-40 px-4 py-3 text-right">
                    {deciding && (
                      <ButtonInput label="Accept" size="sm" isSubmit={false} classes="" onClick={() => onAccept(bid)} disabled={busy} />
                    )}
                    {won && <CheckCircleIcon className="ml-auto h-5 w-5 text-green-600" aria-label="Winning bid" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function PartProBidVehicle() {
  const { vehicleId } = useParams();
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { claims } = useAuth();
  const isAdmin = !!claims?.isAdmin;
  const { vehicle, decideLot, reopenLot, endBidding, setDeadline } = usePartProTradeBidVehicle(vehicleId);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [deadline, setDeadlineInput] = useState("");

  const data = vehicle.data?.data;

  useEffect(() => {
    setInfo({ name: data ? data.name : "Bids", desc: data?.vrm ?? "PartPro Trade bids on this vehicle" });
  }, [data?.name, data?.vrm]);

  useEffect(() => {
    if (data) setDeadlineInput(toLocalInput(data.status === "OPEN" ? data.biddingEndsAt : null));
  }, [data?.status, data?.biddingEndsAt]);

  const busy = decideLot.isLoading || reopenLot.isLoading || endBidding.isLoading || setDeadline.isLoading;

  const handlers = {
    onSuccess: (res: { message?: string }) => {
      if (res.message) addNotification({ variant: "success", primaryText: res.message });
      setPending(null);
      setOverrideReason("");
    },
    onError: (err: unknown) =>
      addNotification({
        variant: "error",
        primaryText: "PartPro Trade",
        secondaryText: err instanceof Error ? err.message : "Something went wrong",
      }),
  };

  const confirmPending = () => {
    if (!pending) return;
    if (pending.kind === "accept") {
      decideLot.mutate(
        { lotId: pending.lot.id, bidId: pending.bid.id, overrideReason: overrideReason.trim() || undefined },
        handlers
      );
    } else if (pending.kind === "pass") {
      decideLot.mutate({ lotId: pending.lot.id, bidId: null }, handlers);
    } else {
      endBidding.mutate(undefined, handlers);
    }
  };

  const saveDeadline = () => {
    const parsed = DateTime.fromISO(deadline);
    if (!parsed.isValid || parsed <= DateTime.now()) {
      addNotification({ variant: "error", primaryText: "Pick a date and time in the future" });
      return;
    }
    setDeadline.mutate(parsed.toUTC().toISO()!, handlers);
  };

  if (vehicle.isLoading) return <LoadingWheel />;

  if (vehicle.isError || !data) {
    return (
      <div className="mt-4 rounded-lg bg-white p-6 shadow">
        <h2 className="text-lg font-semibold text-gray-900">Vehicle unavailable</h2>
        <p className="mt-1 text-sm text-gray-500">
          {vehicle.error instanceof Error ? vehicle.error.message : "PartPro did not return this vehicle."}
        </p>
        <Link to="/bids" className="mt-3 inline-block text-sm text-primary-600 hover:text-primary-700">
          ← Back to bids
        </Link>
      </div>
    );
  }

  const pill = STATUS_PILL[data.status];
  const anyAwarded = data.lots.some((l) => l.status === "AWARDED");
  const canMoveDeadline = data.status === "OPEN" || (data.status === "CLOSED" && !anyAwarded);
  // Categories with bids first, so the decisions are at the top
  const lots = [...data.lots].sort((a, b) => Number(b.bids.length > 0) - Number(a.bids.length > 0));
  const pendingAcceptNotTop = pending?.kind === "accept" && !pending.bid.isAutoPick;

  const confirmCopy =
    pending?.kind === "accept"
      ? {
          title: `Accept ${PriceFormat(pending.bid.amount)} for ${pending.lot.categoryName}?`,
          message: `${pending.bid.accountName ?? pending.bid.buyerCompanyName} will be emailed your contact details to arrange collection and payment, and the other bidders will be told they were unsuccessful. This can't be undone.`,
          button: "Accept bid",
        }
      : pending?.kind === "pass"
        ? {
            title: `Don't award ${pending.lot.categoryName}?`,
            message: "Every bidder on this category will be told they were unsuccessful. You can change this until the vehicle is completed.",
            button: "Don't award",
          }
        : {
            title: "End bidding now?",
            message: "Buyers can no longer bid or change their bids. You'll then accept the winning bid for each category.",
            button: "End bidding",
          };

  return (
    <div className="mt-4 space-y-4">
      <Link to={`/bids?tab=${data.status === "OPEN" ? "open" : data.status === "CLOSED" ? "closed" : "completed"}`} className="text-sm text-gray-500 hover:text-gray-700">
        ← Back to bids
      </Link>

      <div className="flex flex-col gap-4 rounded-lg bg-white p-4 shadow sm:flex-row">
        {data.photos[0] && <img src={data.photos[0]} alt="" className="h-32 w-48 flex-none rounded object-cover" />}
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900">{data.name}</h2>
            {data.vrm && <span className="text-gray-500">{data.vrm}</span>}
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${pill.classes}`}>{pill.label}</span>
          </div>
          <p className="text-sm text-gray-600">
            {data.status === "OPEN" && data.biddingEndsAt && (
              <>
                Bidding ends {formatDateTime(data.biddingEndsAt, DateTime.DATETIME_MED)} (
                {DateTime.fromISO(data.biddingEndsAt).toRelative()}).{" "}
              </>
            )}
            {data.closedAt && data.status !== "OPEN" && <>Bidding closed {formatDateTime(data.closedAt, DateTime.DATETIME_MED)}. </>}
            {data.commissionPct != null && <>PartPro commission {data.commissionPct}% of each accepted bid.</>}
          </p>
          {data.coreVehicleId && (
            <Link to={`/vehicles/${data.coreVehicleId}`} className="text-sm text-primary-600 hover:text-primary-700">
              View vehicle
            </Link>
          )}
        </div>

        <div className="flex flex-col items-stretch gap-2 sm:w-72">
          {data.status === "OPEN" && isAdmin && (
            <ButtonInput
              label="End Bidding"
              isSubmit={false}
              classes="!bg-red-500 hover:!bg-red-600"
              onClick={() => setPending({ kind: "end" })}
              disabled={busy}
            />
          )}
          {canMoveDeadline && (
            <div>
              <label htmlFor="biddingEndsAt" className="block text-xs font-medium text-gray-600">
                {data.status === "OPEN" ? "Change deadline" : "Reopen bidding until"}
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  id="biddingEndsAt"
                  type="datetime-local"
                  value={deadline}
                  onChange={(e) => setDeadlineInput(e.target.value)}
                  className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <ButtonInput
                  label={data.status === "OPEN" ? "Save" : "Reopen"}
                  isSubmit={false}
                  classes=""
                  onClick={saveDeadline}
                  isLoading={setDeadline.isLoading}
                  disabled={busy}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {data.status === "OPEN" && (
        <p className="text-sm text-gray-500">
          Bids are sealed - buyers only see their own. {isAdmin ? "You" : "An admin"} can accept bids once bidding ends.
        </p>
      )}
      {data.status === "CLOSED" && !isAdmin && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Only an admin can accept bids. Ask an admin to review this vehicle.
        </p>
      )}

      {lots.map((lot) => (
        <LotCard
          key={lot.id}
          lot={lot}
          vehicle={data}
          canDecide={isAdmin}
          busy={busy}
          onAccept={(bid) => setPending({ kind: "accept", lot, bid })}
          onPass={() => setPending({ kind: "pass", lot })}
          onReopen={() => reopenLot.mutate(lot.id, handlers)}
        />
      ))}

      <ConfirmModal
        open={!!pending}
        setOpen={(open: boolean) => !open && setPending(null)}
        title={confirmCopy.title}
        message={confirmCopy.message}
        confirmButtonText={confirmCopy.button}
        onConfirm={confirmPending}
        isLoading={decideLot.isLoading || endBidding.isLoading}
      >
        {pendingAcceptNotTop && (
          <div className="mt-4">
            <label htmlFor="overrideReason" className="block text-sm font-medium text-gray-700">
              This isn&apos;t the highest bid - reason (optional)
            </label>
            <input
              id="overrideReason"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              maxLength={500}
              className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        )}
      </ConfirmModal>
    </div>
  );
}
